import { useState } from 'react';
import { useTranslations } from 'use-intl';
import { useNavigate } from '@tanstack/react-router';
import {
  AlertDialog,
  Body,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  I,
  PageHeader,
  PageHeaderSkeleton,
  Pill,
  Row,
  Stack,
  useDelayedLoading,
} from '@leanin/ui';
import { Tooltip } from '@leanin/ui/client';
import { LoadErrorState } from '../../states/index';
import { InviteMemberModal } from '../../group/InviteMemberModal';
import { GroupPendingInvitesCard } from '../../group/admin/GroupPendingInvitesCard';
import { CopyJoinLinkButton } from '../../group/admin/CopyJoinLinkButton';
import { GroupArchivedCallout } from '../../group/GroupArchivedCallout';
import type { GroupRosterMember } from '../../../lib/views/groups';
import { useGroupRoster, useNetwork } from '../../../lib/queries/groups';
import { useFormatDate } from '../../../lib/format-date.client';
import {
  useLeaveGroup,
  useRemoveGroupMember,
  useSetGroupMemberRole,
} from '../../../lib/mutations/groups';
import { useGroupLeaveFlow } from '../../group/use-group-leave-flow';
import { formatGroupMemberError } from '../../groups/format-group-member-error';
import { useErrorMessage } from '../../../lib/error-message';
import { useConfirmDialog } from '../../../lib/use-confirm-dialog';
import {
  MembersTablePanel,
  MembersTableSkeleton,
  buildRosterColumns,
  memberName,
} from '../../group/admin/members-table';
import { useRosterExport } from '../../group/admin/use-roster-export';

type Props = {
  readonly networkId: string;
  readonly viewerUserId: string;
  /** Open the invite modal on mount (`?invite=1` deep link). */
  readonly initialInviteOpen?: boolean;
};

export function NetworkMembersSection({ networkId, viewerUserId, initialInviteOpen = false }: Props) {
  const t = useTranslations('Group');
  const tn = useTranslations('Networks');
  const describeError = useErrorMessage();
  const detailQuery = useNetwork(networkId);
  const membersQuery = useGroupRoster(networkId);
  const fmt = useFormatDate();
  const networkName = detailQuery.data?.name ?? tn('membersAdmin.networkNameFallback');
  // Archived Networks are read-only — see CircleMembersSection for the rationale.
  const isArchived = detailQuery.data?.status === 'archived';
  const exportRoster = useRosterExport(networkName, 'network');

  const navigate = useNavigate();
  const setRoleMutation = useSetGroupMemberRole();
  const removeMutation = useRemoveGroupMember();
  const leaveMutation = useLeaveGroup();

  const [error, setError] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(initialInviteOpen);
  const remove = useConfirmDialog<GroupRosterMember>();
  const demote = useConfirmDialog<GroupRosterMember>();

  const leaveFlow = useGroupLeaveFlow({
    leave: (handlers) =>
      leaveMutation.mutate(
        { id: networkId, kind: 'network' },
        {
          onSuccess: () => {
            navigate({ to: '/networks' });
            handlers.onSuccess();
          },
          onError: handlers.onError,
        },
      ),
    fallbackMessage: tn('membersAdmin.errors.leaveFailed'),
  });

  const isPending = setRoleMutation.isPending || removeMutation.isPending;

  const handlePromote = (m: GroupRosterMember) => {
    setError(null);
    setRoleMutation.mutate(
      { groupId: networkId, kind: 'network', userId: m.userId, role: 'leader' },
      {
        onError: (err) =>
          setError(formatGroupMemberError(err, describeError, tn('membersAdmin.errors.roleUpdateFailed'), t('membersAdmin.lastLeaderNudge'))),
      },
    );
  };

  const confirmDemote = () => {
    if (demote.target === null) return;
    const target = demote.target;
    setError(null);
    setRoleMutation.mutate(
      { groupId: networkId, kind: 'network', userId: target.userId, role: 'member' },
      {
        onSettled: () => demote.close(),
        onError: (err) =>
          setError(formatGroupMemberError(err, describeError, tn('membersAdmin.errors.roleUpdateFailed'), t('membersAdmin.lastLeaderNudge'))),
      },
    );
  };

  const confirmRemove = () => {
    if (remove.target === null) return;
    const target = remove.target;
    setError(null);
    removeMutation.mutate(
      { groupId: networkId, userId: target.userId },
      {
        onSettled: () => remove.close(),
        onError: (err) =>
          setError(formatGroupMemberError(err, describeError, tn('membersAdmin.errors.removeFailed'), t('membersAdmin.lastLeaderNudge'))),
      },
    );
  };

  // Gate the body on BOTH queries — `isArchived` comes from the detail query,
  // so rendering before it settles would flash management controls on an
  // archived Network. See CircleMembersSection for the full rationale.
  const showSkeleton = useDelayedLoading(
    (membersQuery.data === undefined && !membersQuery.isError) ||
      detailQuery.isLoading,
  );
  if (membersQuery.isError) {
    return <LoadErrorState title={tn('membersAdmin.errors.loadFailedTitle')} error={membersQuery.error} />;
  }
  if (showSkeleton) {
    return (
      <Stack gap="lg">
        <PageHeaderSkeleton withAction />
        <MembersTableSkeleton rows={6} />
      </Stack>
    );
  }
  if (membersQuery.data === undefined || detailQuery.isLoading) return null;

  const allMembers = membersQuery.data.items;

  // Leave-button gating: hide entirely if the viewer isn't on the roster
  // (e.g. platform admin viewing without joining), disabled + tooltip if
  // they're the sole leader, enabled otherwise.
  const viewerMembership = allMembers.find((m) => m.userId === viewerUserId);
  const viewerIsOnRoster = viewerMembership !== undefined;
  const leaderCount = allMembers.filter((m) => m.role === 'leader').length;
  const viewerIsOnlyLeader =
    viewerMembership?.role === 'leader' && leaderCount <= 1;

  const columns = buildRosterColumns<GroupRosterMember>({
    viewerUserId,
    t,
    fmt,
    groupKind: 'network',
    renderRole: (m) => (
      <Pill tone={m.role === 'leader' ? 'burgundy' : 'neutral'}>
        {m.role === 'leader' ? tn('membersAdmin.roleLeader') : tn('membersAdmin.roleMember')}
      </Pill>
    ),
    // Archived Networks are read-only — drop the per-row management menu.
    renderActions: isArchived
      ? null
      : (m, { name }) => {
      const isLeader = m.role === 'leader';
      return (
        <Row gap="xs" align="center" justify="flex-end">
          <DropdownMenu
            align="end"
            trigger={
              <Button
                variant="ghost"
                size="sm"
                icon={I.more}
                aria-label={tn('membersAdmin.manageAria', { name })}
              />
            }
          >
            {!isLeader ? (
              <DropdownMenuItem
                onSelect={() => handlePromote(m)}
                disabled={isPending}
              >
                {tn('membersAdmin.makeLeader')}
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                onSelect={() => demote.request(m)}
                disabled={isPending}
              >
                {tn('membersAdmin.demote')}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              destructive
              disabled={isPending}
              onSelect={() => remove.request(m)}
            >
              {tn('membersAdmin.removeFromNetwork')}
            </DropdownMenuItem>
          </DropdownMenu>
        </Row>
      );
    },
  });

  return (
    <Stack gap="lg">
      <PageHeader
        title={tn('membersAdmin.title')}
        subtitle={tn('membersAdmin.subtitle')}
        actions={
          <Row gap="sm" align="center" wrap>
            {!viewerIsOnRoster ? null : viewerIsOnlyLeader ? (
              <Tooltip content={tn('membersAdmin.onlyLeaderTooltip')}>
                <span tabIndex={0}>
                  <Button variant="secondary" size="md" disabled>
                    {tn('membersAdmin.leave')}
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Button
                variant="secondary"
                size="md"
                onClick={leaveFlow.openConfirm}
              >
                {tn('membersAdmin.leave')}
              </Button>
            )}
            {isArchived ? null : (
              <>
                <CopyJoinLinkButton groupId={networkId} />
                <Button
                  variant="primary"
                  size="md"
                  icon={I.plus}
                  onClick={() => setInviteOpen(true)}
                >
                  {tn('membersAdmin.addMember')}
                </Button>
              </>
            )}
          </Row>
        }
      />

      {isArchived ? (
        <GroupArchivedCallout groupId={networkId} kind="network" viewerIsLeader />
      ) : null}

      {membersQuery.data.truncated ? (
        <Body size="sm" color="destructive">
          {t('members.truncated', { maxRows: membersQuery.data.maxRows })}
        </Body>
      ) : null}

      <MembersTablePanel<GroupRosterMember>
        members={allMembers}
        columns={columns}
        t={t}
        searchPlaceholder={tn('membersAdmin.searchPlaceholder')}
        emptyMessage={tn('membersAdmin.emptyMatch')}
        totalCount={detailQuery.data?.memberCount ?? allMembers.length}
        countSummary={(shown, total) => (
          <Body size="sm" color="mute">
            {tn('membersAdmin.countSummary', { shown, total })}
          </Body>
        )}
        onExport={exportRoster}
        exportLabel={t('members.export')}
        groupKind="network"
      />

      {error !== null ? (
        <span role="alert" aria-live="polite">
          <Body size="sm" color="destructive">{error}</Body>
        </span>
      ) : null}

      {isArchived ? null : (
        <GroupPendingInvitesCard kind="network" groupId={networkId} />
      )}

      {isArchived ? null : (
        <InviteMemberModal
          open={inviteOpen}
          onOpenChange={setInviteOpen}
          kind="network"
          groupId={networkId}
          groupName={networkName}
        />
      )}

      <AlertDialog
        open={remove.open}
        onOpenChange={remove.onOpenChange}
        title={
          remove.target !== null
            ? tn('membersAdmin.removeDialog.title', { name: memberName(remove.target) })
            : tn('membersAdmin.removeDialog.titleFallback')
        }
        description={
          remove.target !== null
            ? tn('membersAdmin.removeDialog.description', { name: memberName(remove.target) })
            : ''
        }
        actionLabel={tn('membersAdmin.removeDialog.action')}
        cancelLabel={tn('membersAdmin.removeDialog.cancel')}
        destructive
        onAction={confirmRemove}
      />

      <AlertDialog
        open={demote.open}
        onOpenChange={demote.onOpenChange}
        title={
          demote.target !== null
            ? tn('membersAdmin.demoteDialog.title', { name: memberName(demote.target) })
            : tn('membersAdmin.demoteDialog.titleFallback')
        }
        description={tn('membersAdmin.demoteDialog.description')}
        actionLabel={tn('membersAdmin.demoteDialog.action')}
        cancelLabel={tn('membersAdmin.demoteDialog.cancel')}
        destructive
        onAction={confirmDemote}
      />

      <AlertDialog
        open={leaveFlow.confirmOpen}
        onOpenChange={leaveFlow.setConfirmOpen}
        title={tn('membersAdmin.leaveDialog.title')}
        description={tn('membersAdmin.leaveDialog.description')}
        actionLabel={tn('membersAdmin.leaveDialog.action')}
        cancelLabel={tn('membersAdmin.leaveDialog.cancel')}
        destructive
        onAction={leaveFlow.confirmLeave}
      />
      {leaveFlow.error !== null ? (
        <Body size="sm" color="destructive">{leaveFlow.error}</Body>
      ) : null}
    </Stack>
  );
}
